import { useEffect, useRef } from 'react';
import apiEndpointUtils from 'src/utils/apiEndpointUtils';
import { useVesselStore } from 'src/context/VesselContext';
import useApiEndpoints from 'src/context/useApiEndpoints';
import { useAuthStore } from 'src/context/AuthContext';
import useApi from '../useApi';
import { buildQueryParams } from '../endpoints/queryParamsHelper';
import { StowagePlanChange } from 'src/types/ViewModel/Stowage/StowagePlanChange';
import { StowagePlan } from 'src/types/ViewModel/Stowage/StowagePlan';
import { StowagePlanBM } from 'src/types/BusinessModel/Stowage/StowagePlanBM';
import { copyNewStowagePlanMetaData, createEmpyStowagePlan, mapStowagePlan, mapStowagePlanBM } from 'src/factory/Stowage/StowagePlanFactory';
import { useStowageUpdateHander } from 'src/app/useStowageUpdateHander';
import {
    addCargoesToStowagePlanChanges,
    addPortCallsToStowagePlanChanges,
    addViolationsToStowagePlanChanges,
    mapStowagePlanChangeBMs,
    mapStowagePlanChanges,
} from 'src/factory/Stowage/StowagePlanChanges/StowagePlanChangeFactory';
import { StowagePlanChangeBM } from 'src/types/BusinessModel/Stowage/StowagePlanChangeBM';
import { UUID } from 'crypto';
import { getDateNow } from 'odfjell-date-time-utils/dateTimeUtils';
import { useViolationsStore } from 'src/context/ViolationsContext';
import { StowagePlanRulesManager, StowageRulesOptions } from 'src/utils/stowage/stowageRulesManager/stowagePlanRulesManager';
import { useRotationStore } from 'src/context/RotationContext';
import { updateViolations } from 'src/utils/stowage/violations/violationsUtils';
import { StowagePlanTypeEnum } from 'src/types/ViewModel/Stowage/Enums/StowagePlanTypeEnum';
import useStowageQueryParamsUtils from 'src/utils/stowage/useStowageQueryParamsUtils';
import { useStowagePlanStore } from 'src/context/StowagePlanContext';

interface PendingSave {
    stowagePlan: StowagePlan;
    stowagePlanChanges: StowagePlanChange[];
    resolve: (result: SaveStowageDataResult) => void;
    reject: (error: Error) => void;
}

interface StowageApiQueryParams {
    imoNumber: number;
    connectionId: string;
    stowagePlanId?: string;
}

interface ProcessStowagePlanResult {
    updatedStowagePlan: StowagePlan;
    currentStowagePlans?: StowagePlan[];
    updatedChanges: StowagePlanChange[];
    wasNewPlanCreated?: boolean;
}

interface SaveStowageDataResult {
    savedStowagePlanChanges: StowagePlanChange[];
    updatedStowagePlan: StowagePlan;
}

export default () => {
    const { apiEndpoints } = useApiEndpoints();
    const { selectedVessel } = useVesselStore();
    const { violations } = useViolationsStore();
    const { signalRConnectionId, loggedInUser } = useAuthStore();
    const { processApiEndpointError } = apiEndpointUtils();
    const { state, postApiData } = useApi();
    const { updateStowagePlansOnAdd, updateStowagePlanChanges, updateActiveStowagePlan, updateSelectedStowageItems } = useStowageUpdateHander();
    const { coatingCompatibilities, uscgCompatibilities } = useRotationStore();
    const { stowagePlans, setStowagePlans, setActiveStowagePlan } = useStowagePlanStore();
    const { setQueryParamStowagePlan } = useStowageQueryParamsUtils();

    // Save queue to prevent concurrent saves that can cause duplicate stowage plan inserts
    const isSavingRef = useRef<boolean>(false);
    const saveQueueRef = useRef<PendingSave[]>([]);

    useEffect(() => {
        if (state.status !== 'success') {
            processApiEndpointError(state);
        }
    }, [state]);

    const updateStowagePlanMetadata = (plan: StowagePlan): StowagePlan => {
        const _updatedPlan = { ...plan };
        _updatedPlan.lastUpdatedUtc = getDateNow();
        _updatedPlan.lastUpdatedBy = loggedInUser.userName;
        return _updatedPlan;
    };

    const getStowageApiQueryParams = (stowagePlanId?: string): StowageApiQueryParams => {
        const queryParams: StowageApiQueryParams = {
            imoNumber: selectedVessel.imoNumber,
            connectionId: signalRConnectionId,
        };

        if (stowagePlanId) {
            queryParams.stowagePlanId = stowagePlanId;
        }

        return queryParams;
    };

    const addStowagePlan = async (stowagePlan: StowagePlan) => {
        const updatedPlan = updateStowagePlanMetadata(stowagePlan);
        const _bm = mapStowagePlanBM(updatedPlan);
        const result = await postApiData({
            endpoint: apiEndpoints['addStowagePlan'].url,
            data: _bm,
            queryParam: buildQueryParams(getStowageApiQueryParams(updatedPlan.stowagePlanGuid)),
            shouldReturnError: true,
            callInBackground: true,
        });

        if (result.status && result.status !== 200) {
            throw new Error(apiEndpoints['addStowagePlan'].errorMessage);
        }

        const bm = result as StowagePlanBM;
        const _stowagePlan = mapStowagePlan(bm);

        return updateStowagePlansOnAdd(_stowagePlan);
    };

    const updateStowagePlan = async (stowagePlan: StowagePlan, skipSetActiveStowagePlan?: boolean) => {
        const updatedPlan = updateStowagePlanMetadata(stowagePlan);
        const _bm = mapStowagePlanBM(updatedPlan);
        const result = await postApiData({
            endpoint: apiEndpoints['updateStowagePlan'].url,
            data: _bm,
            queryParam: buildQueryParams(getStowageApiQueryParams(updatedPlan.stowagePlanGuid)),
            shouldReturnError: true,
            callInBackground: true,
        });

        if (result.status && result.status !== 200) {
            throw new Error(apiEndpoints['updateStowagePlan'].errorMessage);
        }

        const bm = result as StowagePlanBM;
        const _stowagePlan = mapStowagePlan(bm);

        return updateStowagePlansOnAdd(_stowagePlan, stowagePlan, skipSetActiveStowagePlan);
    };

    const checkViolations = (stowagePlan: StowagePlan) => {
        const stowageRulesManager = new StowagePlanRulesManager();
        const newViolations = stowageRulesManager.verifyStowageRules(
            {} as StowageRulesOptions,
            stowagePlan,
            selectedVessel,
            null,
            null,
            coatingCompatibilities,
            uscgCompatibilities
        );
        return updateViolations(violations ?? [], newViolations);
    };

    const executeSaveStowageData = async (stowagePlan: StowagePlan, stowagePlanChanges: StowagePlanChange[]): Promise<SaveStowageDataResult> => {
        if (!stowagePlanChanges?.length) {
            return { savedStowagePlanChanges: [], updatedStowagePlan: stowagePlan };
        }

        const originalStowagePlanGuid = stowagePlan.stowagePlanGuid;
        let currentStowagePlans = [...stowagePlans];
        let accumulatedChanges: StowagePlanChange[] = [];

        const processedViolations = processViolations(stowagePlan);
        accumulatedChanges = [...processedViolations];

        const processResult = await processStowagePlanState(stowagePlan, accumulatedChanges);
        stowagePlan = processResult.updatedStowagePlan;
        currentStowagePlans = processResult.currentStowagePlans || currentStowagePlans;
        accumulatedChanges = processResult.updatedChanges;
        const wasNewPlanCreated = processResult.wasNewPlanCreated ?? false;

        const allChanges = [...accumulatedChanges, ...stowagePlanChanges];

        // Update stowagePlanGuid on all changes when a new plan was created
        // This ensures the changes are associated with the new child plan, not the original master plan
        if (wasNewPlanCreated) {
            allChanges.forEach((change) => {
                change.stowagePlanGuid = stowagePlan.stowagePlanGuid;
            });
        }

        setTransactionGuid(allChanges, crypto.randomUUID());

        const changesToSend = mapStowagePlanChangeBMs(allChanges, loggedInUser.fullName, stowagePlan.stowagePlanGuid);

        // SW dependency gate ensures AddStowagePlan completes before SaveStowageData reaches the backend,
        // so we always fire in background and let the optimistic response handle the UX.
        postApiData({
            endpoint: apiEndpoints['saveStowageData'].url,
            data: changesToSend,
            queryParam: buildQueryParams(getStowageApiQueryParams(stowagePlan.stowagePlanGuid.toString())),
            additionalInfo: { changes: allChanges },
            shouldReturnError: true,
            callInBackground: true,
        });

        // Apply changes to local state — must complete before returning so callers
        // (e.g. ChangeLog sync) see the populated plan and don't overwrite it
        const stowagePlanForUpdate = stowagePlan;
        const { updatedStowagePlan: rebuiltPlan } = await updateStowagePlanChanges(allChanges, stowagePlanForUpdate, currentStowagePlans);
        updateSelectedStowageItems(rebuiltPlan);
        handleStowagePlanReference(originalStowagePlanGuid, rebuiltPlan);
        setActiveStowagePlan({ ...rebuiltPlan });

        return { savedStowagePlanChanges: allChanges, updatedStowagePlan: rebuiltPlan };
    };

    const processNextSave = async (): Promise<void> => {
        if (isSavingRef.current || saveQueueRef.current.length === 0) {
            return;
        }

        isSavingRef.current = true;
        const pendingSave = saveQueueRef.current.shift()!;

        try {
            const result = await executeSaveStowageData(pendingSave.stowagePlan, pendingSave.stowagePlanChanges);
            pendingSave.resolve(result);
        } catch (error) {
            pendingSave.reject(error instanceof Error ? error : new Error(String(error)));
        } finally {
            isSavingRef.current = false;
            // Process next queued save if any
            processNextSave();
        }
    };

    const saveStowageData = async (stowagePlan: StowagePlan, stowagePlanChanges: StowagePlanChange[]): Promise<SaveStowageDataResult> => {
        // Fast path: if no changes, return immediately without queuing
        if (!stowagePlanChanges?.length) {
            return { savedStowagePlanChanges: [], updatedStowagePlan: stowagePlan };
        }

        return new Promise<SaveStowageDataResult>((resolve, reject) => {
            saveQueueRef.current.push({
                stowagePlan,
                stowagePlanChanges,
                resolve,
                reject,
            });
            processNextSave();
        });
    };

    const processViolations = (stowagePlan: StowagePlan): StowagePlanChange[] => {
        const violationChanges: StowagePlanChange[] = [];
        const checkViolationsResponse = checkViolations(stowagePlan);

        if (checkViolationsResponse.newViolations.length > 0) {
            addViolationsToStowagePlanChanges(violationChanges, stowagePlan.stowagePlanGuid, checkViolationsResponse.newViolations, false);
        }

        if (checkViolationsResponse.deletedViolations.length > 0) {
            addViolationsToStowagePlanChanges(violationChanges, stowagePlan.stowagePlanGuid, checkViolationsResponse.deletedViolations, true);
        }

        return violationChanges;
    };

    const processStowagePlanState = async (stowagePlan: StowagePlan, changes: StowagePlanChange[]): Promise<ProcessStowagePlanResult> => {
        const updatedPlan = updateStowagePlanMetadata(stowagePlan);
        const updatedChanges = [...changes];

        if (updatedPlan.isNewStowagePlan) {
            const { updatedStowagePlan } = await addStowagePlan(updatedPlan);
            const processedPlan = copyNewStowagePlanMetaData(stowagePlan, updatedStowagePlan);

            addPortCallsToStowagePlanChanges(updatedChanges, processedPlan.stowagePlanGuid, processedPlan.rotation, false);
            addCargoesToStowagePlanChanges(updatedChanges, processedPlan.stowagePlanGuid, processedPlan.cargoes, false);

            return { updatedStowagePlan: processedPlan, updatedChanges, wasNewPlanCreated: true };
        } else if (updatedPlan.type === StowagePlanTypeEnum.Master || updatedPlan.type === StowagePlanTypeEnum.Published) {
            const emptyPlan = createEmpyStowagePlan(
                selectedVessel.imoNumber,
                loggedInUser.fullName,
                updatedPlan.stowagePlanGuid,
                updatedPlan.title,
                updatedPlan.masterPlanVersion ?? 0
            );
            const updatedEmptyPlan = updateStowagePlanMetadata(emptyPlan);

            const { updatedStowagePlan, updatedStowagePlans } = await addStowagePlan(updatedEmptyPlan);
            return {
                updatedStowagePlan,
                currentStowagePlans: updatedStowagePlans,
                updatedChanges,
                wasNewPlanCreated: true,
            };
        } else {
            // Find the current plan in the store and update it
            const existingPlanIndex = stowagePlans.findIndex((plan) => plan.stowagePlanGuid === updatedPlan.stowagePlanGuid);

            if (existingPlanIndex !== -1) {
                const updatedStowagePlans = [...stowagePlans];
                updatedStowagePlans[existingPlanIndex] = updatedPlan;
                setStowagePlans([...updatedStowagePlans]);
            }
        }

        return {
            updatedStowagePlan: updatedPlan,
            updatedChanges,
        };
    };

    const handleStowagePlanReference = (originalGuid: UUID, updatedPlan: StowagePlan): void => {
        if (updatedPlan.stowagePlanGuid !== originalGuid) {
            setQueryParamStowagePlan(updatedPlan.stowagePlanGuid, ['portCallGuid', 'berthCallGuid', 'cargoOperationGuid']);
        } else {
            updateActiveStowagePlan({ ...updatedPlan });
        }
    };

    const setTransactionGuid = (stowagePlanChanges: StowagePlanChange[], transactionGuid: UUID): void =>
        stowagePlanChanges.forEach((spc) => (spc.transactionGuid = transactionGuid));

    return {
        addStowagePlan,
        updateStowagePlan,
        saveStowageData,
    };
};
